import { dpiMax } from "@eweadn-qiankun/shared-utils";
import type { MouseSolutionProvider } from "./mouseIdentity";

export interface LODOption {
  value: number;
  label: string;
}

/**
 * 展立 PAW3955 lift_cutoff（CMD 0x06/0x16 偏移 [4]）。
 * 协议值不按毫米线性编码。界面 0.7~1.7mm、步进 0.1。
 * 1.7mm 不发协议表里的 6，直接用 2.0mm 档的协议值 2。
 */
const LOD_RULES_3955_ZHANLI: LODOption[] = [
  { value: 0, label: "0.7mm" },
  { value: 7, label: "0.8mm" },
  { value: 3, label: "0.9mm" },
  { value: 1, label: "1.0mm" },
  { value: 8, label: "1.1mm" },
  { value: 4, label: "1.2mm" },
  { value: 9, label: "1.3mm" },
  { value: 5, label: "1.4mm" },
  { value: 10, label: "1.5mm" },
  { value: 11, label: "1.6mm" },
  { value: 2, label: "1.7mm" },
];

export const ZHANLI_3955_LOD_MM_MIN = 0.7;
export const ZHANLI_3955_LOD_MM_MAX = 1.7;
export const ZHANLI_3955_LOD_MM_STEP = 0.1;
/** 协议值 2，界面显示 1.7mm */
export const ZHANLI_3955_LOD_DEVICE_DEFAULT = 2;

const zhanli3955LodMmOf = (option: LODOption): number =>
  Math.round(parseFloat(option.label) * 10) / 10;

export const clampZhanli3955LodMm = (mm: number): number => {
  const clamped = Math.min(
    ZHANLI_3955_LOD_MM_MAX,
    Math.max(ZHANLI_3955_LOD_MM_MIN, Number(mm) || ZHANLI_3955_LOD_MM_MIN)
  );
  const snapped =
    ZHANLI_3955_LOD_MM_MIN +
    Math.round((clamped - ZHANLI_3955_LOD_MM_MIN) / ZHANLI_3955_LOD_MM_STEP) *
      ZHANLI_3955_LOD_MM_STEP;
  return Math.round(snapped * 10) / 10;
};

export const zhanli3955LodDeviceValueToMm = (deviceValue: number): number => {
  const v = Math.round(Number(deviceValue));
  const hit = LOD_RULES_3955_ZHANLI.find((option) => option.value === v);
  if (hit) return zhanli3955LodMmOf(hit);
  return zhanli3955LodMmOf(
    LOD_RULES_3955_ZHANLI.find(
      (option) => option.value === ZHANLI_3955_LOD_DEVICE_DEFAULT
    )!
  );
};

export const zhanli3955LodMmToDeviceValue = (mm: number): number => {
  const snapped = clampZhanli3955LodMm(mm);
  const hit = LOD_RULES_3955_ZHANLI.find(
    (option) => zhanli3955LodMmOf(option) === snapped
  );
  return hit?.value ?? ZHANLI_3955_LOD_DEVICE_DEFAULT;
};

/** 匠盟 PAW3955：协议值 1~11 ↔ 0.7~1.7mm（步进 0.1），默认 1 → 0.7mm */
export const JIANGMENG_3955_LOD_DEVICE_MIN = 1;
export const JIANGMENG_3955_LOD_DEVICE_MAX = 11;
export const JIANGMENG_3955_LOD_DEVICE_DEFAULT = 1;

export const JIANGMENG_3955_LOD_MM_MIN = 0.7;
export const JIANGMENG_3955_LOD_MM_MAX = 1.7;
export const JIANGMENG_3955_LOD_MM_STEP = 0.1;

const LOD_RULES_3955: LODOption[] = Array.from(
  { length: JIANGMENG_3955_LOD_DEVICE_MAX },
  (_, i) => {
    const value = i + JIANGMENG_3955_LOD_DEVICE_MIN;
    const mm = JIANGMENG_3955_LOD_MM_MIN + i * JIANGMENG_3955_LOD_MM_STEP;
    return { value, label: `${mm.toFixed(1)}mm` };
  }
);

export const isJiangmeng3955LodSlider = (
  sensor: string,
  provider: MouseSolutionProvider
): boolean => provider === "jiangmeng" && String(sensor).trim() === "3955";

export const clampJiangmeng3955LodMm = (mm: number): number => {
  const clamped = Math.min(
    JIANGMENG_3955_LOD_MM_MAX,
    Math.max(JIANGMENG_3955_LOD_MM_MIN, Number(mm) || JIANGMENG_3955_LOD_MM_MIN)
  );
  const step = JIANGMENG_3955_LOD_MM_STEP;
  const snapped =
    JIANGMENG_3955_LOD_MM_MIN +
    Math.round((clamped - JIANGMENG_3955_LOD_MM_MIN) / step) * step;
  return Math.round(snapped * 10) / 10;
};

export const clampJiangmeng3955LodDeviceValue = (
  deviceValue: number
): number => {
  const v = Math.round(Number(deviceValue));
  if (
    v >= JIANGMENG_3955_LOD_DEVICE_MIN &&
    v <= JIANGMENG_3955_LOD_DEVICE_MAX
  ) {
    return v;
  }
  return JIANGMENG_3955_LOD_DEVICE_DEFAULT;
};

export const jiangmeng3955LodDeviceValueToMm = (
  deviceValue: number
): number => {
  const v = clampJiangmeng3955LodDeviceValue(deviceValue);
  return clampJiangmeng3955LodMm(
    JIANGMENG_3955_LOD_MM_MIN + (v - 1) * JIANGMENG_3955_LOD_MM_STEP
  );
};

export const jiangmeng3955LodMmToDeviceValue = (mm: number): number => {
  const clamped = clampJiangmeng3955LodMm(mm);
  const index = Math.round(
    (clamped - JIANGMENG_3955_LOD_MM_MIN) / JIANGMENG_3955_LOD_MM_STEP
  );
  return clampJiangmeng3955LodDeviceValue(
    index + JIANGMENG_3955_LOD_DEVICE_MIN
  );
};

const LOD_RULES_3950_BEIYING: LODOption[] = [
  { value: 1, label: "0.7mm" },
  { value: 2, label: "1.0mm" },
  { value: 3, label: "2.0mm" },
];

const LOD_RULES_3950_DEFAULT: LODOption[] = [
  { value: 3, label: "0.7mm" },
  { value: 1, label: "1.0mm" },
  { value: 2, label: "2.0mm" },
];

const LOD_RULES_3395: LODOption[] = [
  { value: 1, label: "1.0mm" },
  { value: 2, label: "2.0mm" },
];

const LOD_RULES_3311: LODOption[] = [
  { value: 1, label: "1.0mm" },
  { value: 2, label: "2.0mm" },
];

export const getLodOptionsBySensor = (
  sensor: string,
  provider: MouseSolutionProvider
): LODOption[] => {
  const sensorType = String(sensor).trim();
  switch (sensorType) {
    case "3955A":
      return LOD_RULES_3955_ZHANLI;
    case "3955":
      return LOD_RULES_3955;
    case "3950":
      return provider === "beiying"
        ? LOD_RULES_3950_BEIYING
        : LOD_RULES_3950_DEFAULT;
    case "3395":
      return LOD_RULES_3395;
    case "3311":
      return LOD_RULES_3311;
    default:
      return [];
  }
};

export const resolveMaxDpiBySensor = (
  sensorType: string,
  provider: MouseSolutionProvider,
  deviceId?: string
): number => {
  if (provider === "freq") {
    const id = String(deviceId ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");
    // 与 freqDefaultDpiPreset 机型阶梯一致：g2sea 最高 12800
    if (id === "g2sea") return 12800;
    // freq 的 3395 机型按产品需求封顶 26000
    if (String(sensorType).trim() === "3395") return 26000;
  }
  return dpiMax(sensorType);
};
